import * as React from 'react';
import { cn } from '@heroui/react';
import { OptionButton } from './OptionButton';
import type { QuizGameState, QuizQuestion } from './types';

export interface QuestionViewProps {
  question: QuizQuestion | undefined;
  quizTitle: string;
  gameState: QuizGameState;
  selectedIds: string[];
  currentIndex: number;
  total: number;
  progressPercent: number;
  lastAnswerCorrect: boolean;
  onSelect: (optionId: string) => void;
  onSubmitMultiple: () => void;
  onNext: () => void;
}

export function QuestionView({
  question,
  quizTitle,
  gameState,
  selectedIds,
  currentIndex,
  total,
  progressPercent,
  lastAnswerCorrect,
  onSelect,
  onSubmitMultiple,
  onNext,
}: QuestionViewProps) {
  const isMultiple = question?.type === 'multiple';
  const isLast = currentIndex + 1 >= total;
  const isRevealed = gameState === 'answered' || gameState === 'finished';
  const difficulty = question?.difficulty;
  const difficultyClass =
    difficulty === '简单'
      ? 'border-emerald-500/50 text-emerald-300 bg-emerald-500/10'
      : difficulty === '中等'
        ? 'border-amber-500/50 text-amber-300 bg-amber-500/10'
        : 'border-red-500/40 text-red-300 bg-red-500/10';

  return (
    <div className="flex flex-1 flex-col justify-center p-5">
      <div className="px-2 pb-6 pt-2">
        <div className="mb-3 flex items-end justify-between">
          <h1 className="text-lg font-bold uppercase tracking-widest text-white/80">{quizTitle}</h1>
          <span className="font-mono text-sm text-indigo-300">
            题号 {currentIndex + 1} / {total}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        <div className="mb-6">
          <span
            className={cn(
              'mb-4 inline-block rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider',
              difficultyClass
            )}
          >
            {question?.category || '常识'} • {question?.difficulty || '常规'}
          </span>
          <h2 className="mb-2 text-xl font-bold leading-tight drop-shadow-md md:text-3xl">{question?.title}</h2>
          {question?.description ? (
            <p className="mb-2 whitespace-pre-line text-sm text-white/70">{question.description}</p>
          ) : null}
          {isMultiple && gameState === 'idle' ? (
            <p className="animate-pulse text-sm font-medium text-indigo-300">请选择所有正确选项（多选）</p>
          ) : null}
        </div>

        <div className="mb-6 space-y-1">
          {(question?.options || []).map((option, idx) => (
            <OptionButton
              key={option.id}
              option={option}
              index={idx}
              isSelected={selectedIds.includes(option.id)}
              isRevealed={isRevealed}
              isCorrectAnswer={question?.correctIds.includes(option.id) ?? false}
              isWrongSelection={selectedIds.includes(option.id) && !(question?.correctIds.includes(option.id) ?? false)}
              disabled={isRevealed}
              onSelect={() => onSelect(option.id)}
            />
          ))}
        </div>

        <div className="flex min-h-[6rem] items-end justify-center">
          {gameState === 'answered' ? (
            <div className="w-full space-y-3">
              <div
                className={cn(
                  'rounded-xl border px-4 py-3 backdrop-blur-md',
                  lastAnswerCorrect ? 'border-emerald-500/40 bg-emerald-500/15' : 'border-red-500/40 bg-red-500/15'
                )}
              >
                <div className="mb-2 flex justify-center text-sm font-medium text-white">
                  {lastAnswerCorrect ? '🎉 回答正确！' : '❌ 回答错误！'}
                </div>
                <p className="text-center text-sm leading-relaxed text-white/90">{question?.explanation || '暂无解析'}</p>
              </div>
              <button
                type="button"
                className="w-full rounded-xl bg-white py-4 text-lg font-bold text-indigo-900 shadow-lg transition-transform hover:bg-zinc-100 active:scale-95"
                onClick={onNext}
              >
                {isLast ? '我要交卷' : '下一题'}
              </button>
            </div>
          ) : (
            <>
              {isMultiple && selectedIds.length > 0 ? (
                <button
                  type="button"
                  className="w-full rounded-xl bg-indigo-600 py-4 text-lg font-bold text-white shadow-[0_0_20px_rgba(79,70,229,0.4)] transition-all hover:bg-indigo-500 active:scale-95"
                  onClick={onSubmitMultiple}
                >
                  确认提交
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
